import React, { useState } from 'react';
    import { useNavigate } from 'react-router-dom';
    import { useDonation } from '../context/DonationContext';
    import ItemForm from './ItemForm';
    import FMVManager from './FMVManager';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiTrash2, FiFileText, FiChevronRight, FiCheckCircle, FiInfo, FiCamera, FiSettings, FiPlus, FiEdit2, FiTrendingUp, FiCalendar } = FiIcons;

    function DonationWorkspace({ onComplete }) {
      const { state, dispatch } = useDonation();
      const navigate = useNavigate();
      const [activeTab, setActiveTab] = useState('entry');

      const handleComplete = () => {
        onComplete();
        navigate('/report');
      };

      const incrementDate = (days) => {
        const currentDate = new Date(state.currentDonation.date);
        currentDate.setDate(currentDate.getDate() + days);
        const newDateStr = currentDate.toISOString().split('T')[0];
        dispatch({ type: 'UPDATE_DONATION_DATE', payload: newDateStr });
      };

      return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-6">
            {/* Date Quick Actions */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                  <SafeIcon icon={FiCalendar} className="mr-1.5 w-3 h-3 text-blue-500" />
                  Donation Date
                </label>
                <input 
                  type="date" 
                  value={state.currentDonation.date}
                  onChange={(e) => dispatch({ type: 'UPDATE_DONATION_DATE', payload: e.target.value })}
                  className="bg-transparent text-sm font-bold text-gray-700 outline-none cursor-pointer hover:text-blue-600 transition-colors"
                />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[7, 14, 30, 60].map(days => (
                  <button
                    key={days}
                    onClick={() => incrementDate(days)}
                    className="py-1.5 text-[10px] font-black bg-gray-50 border border-gray-100 text-gray-500 rounded-lg hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all active:scale-95"
                  >
                    +{days} DAYS
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-gray-200">
              <div className="flex space-x-6">
                <button 
                  onClick={() => setActiveTab('entry')}
                  className={`pb-4 text-sm font-bold flex items-center space-x-2 transition-all ${activeTab === 'entry' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <SafeIcon icon={FiPlus} />
                  <span>{state.editingItemId ? 'Edit Item' : 'Add Items'}</span>
                </button>
                <button 
                  onClick={() => setActiveTab('manage')}
                  className={`pb-4 text-sm font-bold flex items-center space-x-2 transition-all ${activeTab === 'manage' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <SafeIcon icon={FiSettings} />
                  <span>Valuation Guide</span>
                </button>
              </div>
            </div>

            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              {activeTab === 'entry' ? <ItemForm /> : <FMVManager />}
            </div>

            <div className="lg:hidden">
              <SummaryList onComplete={handleComplete} onEdit={() => setActiveTab('entry')} />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-6 space-y-6">
              <div className="hidden lg:block">
                <SummaryList onComplete={handleComplete} onEdit={() => setActiveTab('entry')} />
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start space-x-3">
                <SafeIcon icon={FiInfo} className="text-blue-500 w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>IRS Compliance:</strong> Single non-cash donations valued between $500 and $5,000 require Form 8283 Section A. This app tracks items up to the $499 threshold for simplified auditing.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    function SummaryList({ onComplete, onEdit }) {
      const { state, dispatch } = useDonation();
      const handleRemove = (id) => { dispatch({ type: 'REMOVE_ITEM', payload: id }); };
      const handleEdit = (id) => {
        dispatch({ type: 'SET_EDITING_ITEM', payload: id });
        if (onEdit) onEdit();
      };

      const totalItemsQuantity = state.currentDonation.items.reduce((sum, item) => sum + item.totalQuantity, 0);
      const taxSavings = (state.currentDonation.total * (state.taxBracket / 100)).toFixed(2);
      const taxBrackets = [
        { label: '10%', value: 10 }, { label: '12%', value: 12 }, { label: '22%', value: 22 }, { label: '24%', value: 24 }, { label: '32%+', value: 32 }
      ];

      return (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col max-h-[calc(100vh-12rem)]">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Live Summary</h3>
            <span className="text-[10px] font-black text-gray-400 uppercase bg-gray-100 px-2 py-0.5 rounded-md">
              {totalItemsQuantity} Total Qty ({state.currentDonation.items.length} Types)
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar min-h-[200px]">
            {state.currentDonation.items.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <SafeIcon icon={FiCheckCircle} className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm px-4">Your donation list is currently empty</p>
              </div>
            ) : (
              state.currentDonation.items.map((item) => (
                <div key={item.id} className={`group relative bg-white border rounded-xl p-3 transition-all animate-in slide-in-from-right-4 ${state.editingItemId === item.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100' : 'border-gray-100 hover:border-blue-200 hover:shadow-sm'}`} >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {item.photoId ? (
                        <img src={state.photos.find(p => p.id === parseInt(item.photoId))?.url} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <SafeIcon icon={FiCamera} className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold text-gray-900 truncate">{item.itemName}</h4>
                        <span className="text-xs font-bold text-green-600">${item.subtotal.toFixed(2)}</span>
                      </div>
                      <p className="text-[9px] text-gray-500 font-medium uppercase tracking-tight">
                        Qty: {item.totalQuantity} • {item.category}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button onClick={() => handleEdit(item.id)} className={`p-1.5 rounded-lg transition-colors ${state.editingItemId === item.id ? 'text-blue-600 bg-white' : 'text-gray-300 hover:text-blue-500 hover:bg-gray-50'}`} >
                        <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleRemove(item.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors" >
                        <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4">
            <div className="flex items-center justify-between bg-white p-2 rounded-xl border border-gray-100">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Bracket</label>
              <div className="flex bg-gray-100 p-1 rounded-lg">
                {taxBrackets.map((bracket) => (
                  <button key={bracket.value} onClick={() => dispatch({ type: 'SET_TAX_BRACKET', payload: bracket.value })} className={`px-2 py-1 text-[10px] font-bold rounded-md transition-all ${state.taxBracket === bracket.value ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} >
                    {bracket.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500 uppercase">Total Valuation</span>
                <span className="text-xl font-black text-gray-900">${state.currentDonation.total.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100 animate-in zoom-in-95 duration-300">
                <div className="flex items-center space-x-2">
                  <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center shadow-sm shadow-green-200">
                    <SafeIcon icon={FiTrendingUp} className="text-white w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold text-green-700">Estimated Tax Savings</span>
                </div>
                <span className="text-lg font-black text-green-600">${taxSavings}</span>
              </div>
            </div>
            <button onClick={onComplete} disabled={state.currentDonation.items.length === 0} className={`w-full py-3 rounded-xl font-bold flex items-center justify-center space-x-2 shadow-lg transition-all ${state.currentDonation.items.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} >
              <SafeIcon icon={FiFileText} />
              <span>Finalize Receipt</span>
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
      );
    }
    export default DonationWorkspace;